import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { LINKS } from "@/lib/links";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/* ---------- Data ---------- */
const WHY_IBC = [
  {
    sub: "Everyone has a need to finance things.",
    body:
      "At some point in your life, you will need to finance something — a car, a home, a business, an education, an emergency. That need doesn't go away. The question isn't whether you'll participate in the banking system. It's whether you'll be on the profitable side of it or the costly side. Most people spend their entire lives on the costly side. They borrow from banks, pay interest to lenders, and hand money to institutions that turn around and use those same dollars to generate returns for themselves. The banking system is one of the most profitable businesses in the world — and it's profitable largely because of us.",
  },
  {
    sub: "It's not the interest rate. It's the volume.",
    body:
      "When most people think about the cost of borrowing, they focus on the interest rate. But the more important number is the total volume of interest paid over a lifetime. Think about every car you'll ever finance, every loan you'll ever take, every dollar you'll ever send to a creditor. Add all of that up. That's the number that matters — and for most people, it's significant enough to change the trajectory of their financial life if it were redirected. Infinite Banking is about redirecting that flow. Instead of sending interest payments to banks and finance companies, you build a private system that captures that function for yourself. You still finance the things you need. You still make payments. The difference is where those payments go — and who benefits from them.",
  },
  {
    sub: "The vehicle matters.",
    body:
      "The reason a dividend-paying whole life insurance policy from a mutual company is the tool of choice isn't arbitrary. It's because no other asset combines guaranteed growth, liquidity, tax advantages, and the ability to borrow against itself without interrupting compounding — all in one place. It's not about life insurance. It's about building a structure that lets your money do more than one job at a time. This isn't a new idea. It's one of the oldest financial strategies in existence. The question is simply whether you want to start using it.",
  },
];

const FAQS = [
  { q: "What can cash value actually be used for?", a: "Almost anything. People use it to buy cars, fund real estate, cover emergencies, pay for education, start businesses, and bridge gaps between opportunities. There are no restrictions, no required approvals, and no penalties for accessing it. The more relevant question is what you're currently financing — and whether your money could be working harder while you do it." },
  { q: "Does the stock market affect my policy?", a: "No. A dividend-paying whole life policy from a mutual company grows independently of market performance. Your cash value doesn't go up when the market goes up, but it also doesn't go down when the market drops. It compounds steadily, with guarantees, regardless of what's happening on Wall Street. That stability is part of the point." },
  { q: "What happens if I stop paying premiums?", a: "More flexibility than most people expect. Whole life policies designed for Infinite Banking are structured with two components: a base premium and paid-up additions (PUAs). The base premium is required to keep the policy in force — but it's typically a fraction of your total contribution. If life gets tight, you can reduce payments significantly while keeping the system intact. If you stop paying altogether, the policy has built-in mechanisms — including the ability to use accumulated dividends or cash value to cover premiums — that keep it from simply disappearing. This is not a Netflix subscription. It's a contractual asset with real safeguards." },
  { q: "Why would I borrow against my own money?", a: "Because you're not actually borrowing your own money — you're borrowing against it. When you take a policy loan, your cash value stays in place and keeps compounding as if the loan never happened. The loan comes from the insurance company's general fund, using your cash value as collateral. The result is that your money is working in two places simultaneously: growing inside your policy and being put to use in the world. That's the core mechanic that makes this system different from anything else." },
  { q: "How is this different from a 401k or IRA?", a: "They serve different purposes. Qualified plans like 401ks and IRAs are designed around deferred taxation and market-based growth — they come with contribution limits, withdrawal restrictions, age penalties, and required minimum distributions. Infinite Banking operates entirely outside that system. There are no contribution limits, no age restrictions on access, no market dependency, and no government oversight of how or when you use your money. It's a non-qualified asset that predates the IRS by more than a century. The two aren't in competition — but they're not the same thing." },
  { q: "What's the rate of return?", a: "This is the wrong question for IBC — and that's not a dodge, it's an important distinction. Infinite Banking is a banking strategy, not an investment. Asking for the rate of return is like asking what the rate of return is on your checking account. The value isn't in a percentage — it's in liquidity, control, compounding, and the ability to recapture interest you'd otherwise be paying to someone else. That said, whole life does offer guaranteed growth, annual dividends from mutual companies with long track records, and tax-advantaged access to cash value. The full picture is more compelling than any single number." },
  { q: "What if I can't qualify for insurance?", a: "It doesn't necessarily stop you. If your health history makes you ineligible, the policy can be placed on someone you have insurable interest in — a spouse, child, parent, or business partner. You still own the policy, control the cash value, and implement the strategy. The insured doesn't have to be you. This is a common path forward and one worth exploring before assuming the door is closed." },
  { q: "How does this compare to \"buy term and invest the rest\"?", a: "They're solving different problems. \"Buy term and invest the rest\" is an investment strategy focused on market returns. Infinite Banking isn't an investment strategy at all — it's a banking strategy focused on how you finance your life. Term insurance expires. It builds nothing. And \"invest the rest\" only works if you actually invest it, invest it consistently, and don't need it before retirement. If your goal is to control the flow of money through your life and recapture what you're losing to the banking system, it's a different conversation entirely." },
  { q: "How does cash value growth get taxed?", a: "Cash value inside a properly structured whole life policy grows tax-deferred. You don't pay taxes on the growth each year as it compounds. When you access it through policy loans — which is the standard approach in Infinite Banking — those loans are not considered taxable income. The money moves without triggering a tax event." },
  { q: "Are policy loans taxable?", a: "No. Policy loans are not income — they're loans secured by your cash value. As long as the policy remains in force, you can access your cash value repeatedly without it being reported as taxable income. This is one of the structural advantages that separates whole life from most other financial vehicles." },
  { q: "How does this compare to a Roth IRA on taxes?", a: "Both offer tax-advantaged access to money. The Roth IRA grows tax-free and allows tax-free withdrawals in retirement — but it comes with annual contribution limits, income eligibility requirements, and restrictions on when and how you can access funds. Whole life has no contribution ceiling, no income restrictions, and no age-based access rules. You can also borrow against it at any point without affecting your tax situation. For people who've maxed out their Roth and want another tax-advantaged place to store capital, whole life is often the logical next layer." },
];

const MYTHS = [
  { m: "Whole life insurance is a bad investment.", r: "Whole life insurance isn't an investment — it's a banking tool. Judging it as an investment is like judging a hammer for being a poor screwdriver. When evaluated on its actual purpose — liquidity, stability, guaranteed growth, tax advantages, and the ability to finance your life — it performs in ways no investment account can replicate." },
  { m: "You can do better in the market.", r: "Maybe. Markets can produce strong returns over time — they can also lose 30% in a year. Whole life doesn't compete with the market. It operates alongside it as a stable, guaranteed foundation. Most people who practice IBC still invest. The difference is that their banking system isn't subject to market volatility or the timing of when they need to access money." },
  { m: "Buy term and invest the rest is always better.", r: "This comparison assumes the goal is maximum investment return. If that's your goal, it might be the right framework. But if the goal is to control how money moves through your life — to recapture the interest you're losing to banks, lenders, and finance companies — the comparison doesn't hold. Term insurance expires. It builds nothing. And \"invest the rest\" only works if you actually invest it, consistently, and don't need it before retirement." },
  { m: "My financial advisor told me to avoid whole life.", r: "Most financial advisors are trained in investment management, not banking strategy. They're evaluating whole life against mutual funds — a category mismatch. An advisor who understands Infinite Banking as a banking concept, not an investment, will have a different perspective. It's worth understanding the distinction before accepting the conclusion." },
  { m: "This is only for wealthy people.", r: "The strategy scales to income. You don't need to be wealthy to start — you need a consistent premium you can commit to and a reason to build a private banking system. Many people begin with modest policies and scale from there. The families and institutions that have used this system for generations weren't all starting from wealth. They were building it." },
  { m: "I'm too old to start.", r: "Age affects policy structure and premium costs — but it doesn't eliminate the strategy. Whole life can be placed on a younger insured while you maintain ownership and control. Spouses, children, and grandchildren are all common options. The banking function belongs to the policy owner, not the insured. Age is a factor to plan around — not a reason to stop." },
];

/* ---------- Page ---------- */
export default function FaqPage() {
  return (
    <div className="min-h-screen bg-paper text-nearblack font-sans antialiased">
      <Nav heroVisible={false} />

      {/* Intro */}
      <section className="pt-32 md:pt-44 pb-16 md:pb-20" data-testid="faq-intro">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <p className="eyebrow" data-testid="faq-intro-eyebrow">Questions &amp; Clarity</p>
          <h1
            className="font-serif text-nearblack mt-7 leading-[1.05] text-balance"
            style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 300 }}
            data-testid="faq-intro-headline"
          >
            Honest answers to the questions that{" "}
            <em className="italic text-camel" style={{ fontWeight: 300 }}>actually matter</em>.
          </h1>
          <span className="block w-[60px] h-px bg-camel mt-10" />
          <p className="mt-10 text-nearblack font-light text-[18px] leading-[1.8] max-w-[640px]">
            Most people come to IBC with a mix of curiosity and skepticism. That&apos;s a good
            starting point. This page is here to give you a clear picture — why this system exists,
            how it works, what it isn&apos;t, and what gets misunderstood most often. Read through
            at your own pace.
          </p>
          <p
            className="mt-6 font-serif italic text-camel"
            style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.55rem)", fontWeight: 300 }}
          >
            If you still have questions at the end, that&apos;s what a Money Clarity Call is for.
          </p>
        </div>
      </section>

      {/* Section 1 — Why IBC */}
      <section className="py-20 md:py-28 border-t border-paperdeep" data-testid="faq-why-ibc">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <p className="eyebrow">Why IBC</p>
          <h2
            className="font-serif text-nearblack mt-7 leading-[1.08] text-balance"
            style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.6rem)", fontWeight: 400 }}
          >
            The case for becoming your own{" "}
            <em className="italic text-camel">banker</em>.
          </h2>
          <div className="mt-14 space-y-12">
            {WHY_IBC.map((s, i) => (
              <article key={i} data-testid={`faq-whyibc-${i + 1}`}>
                <h3
                  className="font-serif text-nearblack leading-[1.2]"
                  style={{ fontSize: "clamp(1.35rem, 2.1vw, 1.65rem)", fontWeight: 400 }}
                >
                  {s.sub}
                </h3>
                <p className="mt-5 text-nearblack/85 font-light text-[17px] leading-[1.85]">
                  {s.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2 — FAQ accordion */}
      <section className="py-20 md:py-28 border-t border-paperdeep" data-testid="faq-questions">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <p className="eyebrow">Frequently Asked Questions</p>
          <h2
            className="font-serif text-nearblack mt-7 leading-[1.08] text-balance"
            style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.6rem)", fontWeight: 400 }}
          >
            The questions most people have before{" "}
            <em className="italic text-camel">they&apos;re ready to ask them</em>.
          </h2>

          <Accordion
            type="single"
            collapsible
            className="mt-12 border-t border-paperdeep"
            data-testid="faq-accordion"
          >
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i + 1}`}
                className="border-b border-paperdeep [&_svg]:text-camel"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i + 1}`}
                  className="py-6 hover:no-underline group"
                >
                  <span
                    className="font-serif text-nearblack text-left pr-6 leading-[1.3]"
                    style={{ fontSize: "clamp(1.15rem, 1.9vw, 1.35rem)", fontWeight: 400 }}
                  >
                    {f.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-content-${i + 1}`}
                  className="pb-6 pr-2"
                >
                  <p className="text-nearblack/80 font-light text-[16.5px] leading-[1.85] max-w-[640px]">
                    {f.a}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Section 3 — Myths & Misconceptions */}
      <section className="py-20 md:py-28 border-t border-paperdeep" data-testid="faq-myths">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <p className="eyebrow">Myths &amp; Misconceptions</p>
          <h2
            className="font-serif text-nearblack mt-7 leading-[1.08] text-balance"
            style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.6rem)", fontWeight: 400 }}
          >
            What this <em className="italic text-camel">is not</em>.
          </h2>
          <p
            className="mt-7 font-serif italic text-stonegrey max-w-[620px]"
            style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.3rem)", lineHeight: 1.55, fontWeight: 300 }}
          >
            A lot of what circulates about whole life insurance is either outdated, oversimplified,
            or shaped by people who profit from you thinking differently. Here&apos;s a
            straightforward look at the most common ones.
          </p>

          <Accordion
            type="single"
            collapsible
            className="mt-12 border-t border-paperdeep"
            data-testid="myths-accordion"
          >
            {MYTHS.map((m, i) => (
              <AccordionItem
                key={i}
                value={`myth-${i}`}
                data-testid={`myth-item-${i + 1}`}
                className="border-b border-paperdeep [&_svg]:text-camel"
              >
                <AccordionTrigger
                  data-testid={`myth-trigger-${i + 1}`}
                  className="py-6 hover:no-underline group"
                >
                  <span className="text-left pr-6 flex items-baseline gap-4">
                    <span
                      className="uppercase tracking-[0.22em] text-camel flex-shrink-0"
                      style={{ fontSize: "10.5px", fontWeight: 600 }}
                    >
                      Myth
                    </span>
                    <span
                      className="font-serif text-nearblack leading-[1.3]"
                      style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.3rem)", fontWeight: 400 }}
                    >
                      {m.m}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`myth-content-${i + 1}`}
                  className="pb-6 pr-2"
                >
                  <p className="text-nearblack/80 font-light text-[16.5px] leading-[1.85] max-w-[640px]">
                    {m.r}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-nearblack text-paper py-20 md:py-28" data-testid="faq-cta">
        <div className="max-w-[680px] mx-auto px-6 md:px-10 text-center">
          <p className="eyebrow-light">Ready to Take the Next Step</p>
          <h2
            className="font-serif text-paper mt-7 leading-[1.05]"
            style={{ fontSize: "clamp(2rem, 4.5vw, 2.85rem)", fontWeight: 300 }}
          >
            Still have questions?{" "}
            <em className="italic text-camellight" style={{ fontWeight: 300 }}>Good.</em>
          </h2>
          <p className="mt-8 text-paper/85 text-[17.5px] leading-[1.8] font-light max-w-[560px] mx-auto">
            A Money Clarity Call is a straightforward conversation — no pressure, no pitch. You
            bring your questions, I bring context. We look at your situation specifically and
            figure out whether this makes sense for where you are right now.
          </p>
          <a
            href={LINKS.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-cta-button"
            className="mt-12 btn-camel inline-flex items-center justify-center gap-2 group"
          >
            Book a Money Clarity Call
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <p className="mt-5 text-paper/50 italic text-[13px]">
            30 minutes · Free · No pitch
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
